import { FC, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Box, MenuItem, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField } from '@mui/material';
import { StatusChip } from './PlatformCard';
import { STATUS_LABEL, errorMessage, formatDate, planLabel, platformApi } from './platformApi';

const PLAN_OPTIONS = ['starter', 'business', 'pro', 'enterprise', 'none'];
const STATUS_OPTIONS = ['trialing', 'active', 'grace', 'expired'];
const EXPIRING_OPTIONS = [7, 30, 90];
const PAGE_SIZE = 25;

const PlatformAccounts: FC = () => {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') || '');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const page = Number(params.get('page')) || 1;
  const query = params.toString();

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  // Search as the user types, after a short pause
  useEffect(() => {
    const timer = setTimeout(() => { if (search !== (params.get('search') || '')) setParam('search', search); }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    let active = true;
    platformApi.get(`/accounts?${query}${query ? '&' : ''}limit=${PAGE_SIZE}`)
      .then((response) => { if (active) { setRows(response.data.accounts); setTotal(response.data.total); setError(''); } })
      .catch((err) => { if (active) setError(errorMessage(err)); });
    return () => { active = false; };
  }, [query]);

  const filter = (key: string, label: string, options: (string | number)[], optionLabel: (o: any) => string) => (
    <TextField select size='small' label={label} value={params.get(key) || ''} onChange={(e) => setParam(key, e.target.value)} sx={{ minWidth: 170 }}>
      <MenuItem value=''>All</MenuItem>
      {options.map((o) => <MenuItem key={o} value={String(o)}>{optionLabel(o)}</MenuItem>)}
    </TextField>
  );

  return (
    <>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size='small' label='Search name, email or GSTIN' value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        {filter('plan', 'Plan', PLAN_OPTIONS, (o) => planLabel(o === 'none' ? null : o))}
        {filter('status', 'Status', STATUS_OPTIONS, (o) => STATUS_LABEL[o])}
        {filter('expiringInDays', 'Expiring within', EXPIRING_OPTIONS, (o) => `${o} days`)}
      </Box>
      {/* column headings sit in the grey strip; count and paging at the bottom */}
      <Box sx={{ bgcolor: '#fff', border: '1px solid #dcdcdc', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ bgcolor: '#e9ecf1', '& th': { fontWeight: 600, color: '#111827', borderBottom: '1px solid #dcdcdc', py: 1.25, whiteSpace: 'nowrap' } }}>
              <TableCell>Owner</TableCell>
              <TableCell>Companies</TableCell>
              <TableCell>ERP plan</TableCell>
              <TableCell>ERP status</TableCell>
              <TableCell>ERP valid till</TableCell>
              <TableCell>POS plan</TableCell>
              <TableCell>POS status</TableCell>
              <TableCell>POS valid till</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* one row per owner: the owner holds the plan and all their companies share it */}
            {rows.map((row) => (
              <TableRow key={row._id} hover sx={{ verticalAlign: 'top' }}>
                <TableCell>
                  {row.owner?.name || '-'}
                  <div style={{ color: '#6b7280', fontSize: 12 }}>{row.owner?.email}</div>
                </TableCell>
                <TableCell>
                  {row.companies.map((c: any) => (
                    <div key={c._id}>
                      <Link to={`/platform/accounts/${c._id}`} style={{ color: '#059669', textDecoration: 'none' }}>{c.name}</Link>
                      {row.erp && !c.covered && <span style={{ color: '#b45309', fontSize: 12 }}> · own plan</span>}
                    </div>
                  ))}
                  {row.erp && (
                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                      {row.erp.companies} of {row.erp.companyLimit >= 0 ? row.erp.companyLimit : 'unlimited'} on the plan
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {row.erp ? row.erp.planName || planLabel(row.erp.planCode) : 'Not migrated'}
                  {row.erpPlans > 1 && <div style={{ color: '#b45309', fontSize: 12 }}>{row.erpPlans} separate plans, run the merge</div>}
                </TableCell>
                <TableCell>{row.erp ? <StatusChip status={row.erp.status} /> : '-'}</TableCell>
                <TableCell>{formatDate(row.erp?.endsAt)}</TableCell>
                {/* POS is per company */}
                <TableCell>
                  {row.pos.map((p: any) => (
                    <div key={p.company} style={{ marginBottom: 4 }}>
                      {p.planName || planLabel(p.planCode)}
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{p.company} · {p.outlets} outlet(s)</div>
                    </div>
                  ))}
                  {!row.pos.length && '-'}
                </TableCell>
                <TableCell>{row.pos.map((p: any) => <div key={p.company} style={{ marginBottom: 12 }}><StatusChip status={p.status} /></div>)}{!row.pos.length && '-'}</TableCell>
                <TableCell>{row.pos.map((p: any) => <div key={p.company} style={{ marginBottom: 20 }}>{formatDate(p.endsAt)}</div>)}{!row.pos.length && '-'}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8}>No owners found.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Total: <b>{total.toLocaleString('en-IN')}</b> owner(s)</span>
          <TablePagination
            component='div'
            count={total}
            page={page - 1}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(event, next) => setParam('page', String(next + 1))}
          />
        </Box>
      </Box>
    </>
  );
};

export default PlatformAccounts;
